"use client";

import { stegaClean } from "@sanity/client/stega";
import { useMemo, useState } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useHashAction } from "@/hooks/useHashAction";
import type { ModuleTabbedContent } from "@/lib/types";

interface TabbedContentTabsProps {
  tabs: NonNullable<ModuleTabbedContent["tabs"]>;
  tabContents: React.ReactNode[];
}

function normalizeTabId(tab: ModuleTabbedContent["tabs"][number]): string {
  return stegaClean(tab.moduleId ?? tab.label)
    .toLowerCase()
    .replace(/\s+/g, "-");
}

export function TabbedContentTabs({ tabs, tabContents }: TabbedContentTabsProps) {
  const tabIds = useMemo(() => tabs.map(normalizeTabId), [tabs]);
  const [activeTab, setActiveTab] = useState(tabIds[0] || "");

  // Set active tab when ID matches
  useHashAction(
    tabIds,
    (matchedHash) => {
      const tabIndex = tabIds.indexOf(matchedHash);
      if (tabIndex !== -1) {
        setActiveTab(tabIds[tabIndex]);
      }
    },
    { enabled: tabIds.length > 0 },
  );

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <Tabs
      defaultValue={activeTab}
      value={activeTab}
      onValueChange={handleTabChange}
      className="w-full"
    >
      <TabsList className="mb-4 w-full border-b border-neutral-200">
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab._key}
            value={normalizeTabId(tab)}
            className="hover:text-black! data-[state=active]:text-black data-[state=inactive]:text-neutral-500"
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab, index) => (
        <TabsContent key={tab._key} value={normalizeTabId(tab)} keepMounted>
          {tabContents[index]}
        </TabsContent>
      ))}
    </Tabs>
  );
}
